import React, { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Button, Surface, Text, useTheme } from 'react-native-paper';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';

import { advanceOnboardingState, apiFetch } from '@/src/api/apiService';
import { API_ENDPOINTS } from '@/src/api/config';
import { usePaywallStatus } from '@/src/hooks/usePaywallStatus';
import type { PaywallStatusResponse } from '@/src/types/paywall';

// Stripe hosted Checkout return screen (#207 S1).
//
// The hosted Checkout redirects back to `${return_url}?self_pay=success` (or
// `?self_pay=canceled`) — a SERVER-controlled param set by SelfPayCheckoutView.
// There is deliberately NO client `session_id` here: entitlement is granted and
// tenant-verified server-side by the Stripe webhook (checkout.session.completed,
// which enforces driver_user_id / purchase_intent / self-pay customer
// separation). This screen only REFLECTS the server-authoritative paywall
// status; it never decides entitlement itself and never shows "active" on a
// client's say-so.

type Phase = 'checking' | 'success' | 'pending' | 'canceled' | 'error';

// Entitled paywall states once a self-pay checkout settles (trial starts
// immediately; `active` covers the no-trial / already-charged case).
const ENTITLED_STATES: ReadonlyArray<PaywallStatusResponse['state']> = ['trialing', 'active'];

// Bounded webhook-race handling: the webhook normally lands within a second or
// two of the redirect. Poll a few times, then fall back to a non-blocking
// "pending" state — never a fabricated "active".
const MAX_STATUS_POLLS = 5;
const POLL_INTERVAL_MS = 1500;

const normalizeParam = (value: string | string[] | undefined): string =>
  (Array.isArray(value) ? value[0] : value) || '';

export default function CheckoutReturnScreen() {
  const { t } = useTranslation('onboarding');
  const theme = useTheme();
  const router = useRouter();
  const { refetch } = usePaywallStatus();
  const params = useLocalSearchParams<{ self_pay?: string | string[] }>();
  const selfPay = useMemo(() => normalizeParam(params.self_pay).trim(), [params.self_pay]);

  const [phase, setPhase] = useState<Phase>('checking');

  useEffect(() => {
    if (selfPay === 'canceled') {
      setPhase('canceled');
      return;
    }
    // Only a genuine server redirect (self_pay=success) starts confirmation. A
    // bare route or a stray client-supplied session id must never settle — that
    // was the removed IDOR vector.
    if (selfPay !== 'success') {
      setPhase('error');
      return;
    }

    let cancelled = false;
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const readServerState = async (): Promise<PaywallStatusResponse | undefined> => {
      try {
        const response = await apiFetch<PaywallStatusResponse>(
          API_ENDPOINTS.SUBSCRIPTION_STATUS,
          { method: 'GET' },
          true,
          false, // no cache: we are racing the webhook and need fresh state
        );
        return response.ok ? response.data : undefined;
      } catch {
        return undefined;
      }
    };

    const poll = async () => {
      attempts += 1;
      const status = await readServerState();
      if (cancelled) return;

      if (status?.is_self_pay === true && ENTITLED_STATES.includes(status.state)) {
        // Sync the shared paywall status so the rest of the app unlocks.
        await refetch();
        try {
          if (typeof advanceOnboardingState === 'function') {
            await advanceOnboardingState('DRV-SELF', 'self_pay_active');
          }
        } catch {
          // Webhook entitlement remains authoritative if pointer recording fails.
        }
        if (!cancelled) setPhase('success');
        return;
      }

      if (attempts >= MAX_STATUS_POLLS) {
        if (!cancelled) setPhase('pending');
        return;
      }
      timer = setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [selfPay, refetch]);

  const goToDashboard = () => router.replace('/dashboard/home');
  const goToSelfPay = () => router.replace('/dashboard/self-pay');

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View style={styles.center}>
        <Surface style={styles.content} elevation={1}>
          {phase === 'checking' ? (
            <View testID="checkout-return-checking">
              <ActivityIndicator size="large" color={theme.colors.primary} />
              <Text variant="headlineSmall" style={styles.title}>
                {t('checkoutReturn.checkingTitle')}
              </Text>
              <Text variant="bodyMedium" style={styles.description}>
                {t('checkoutReturn.checkingDescription')}
              </Text>
            </View>
          ) : null}

          {phase === 'success' ? (
            <View testID="checkout-return-success">
              <Text variant="headlineSmall" style={[styles.title, { color: theme.colors.primary }]}>
                {t('checkoutReturn.successTitle')}
              </Text>
              <Text variant="bodyMedium" style={styles.description}>
                {t('checkoutReturn.successDescription')}
              </Text>
              <Button mode="contained" onPress={goToDashboard} style={styles.button}>
                {t('checkoutReturn.returnToDashboard')}
              </Button>
            </View>
          ) : null}

          {phase === 'pending' ? (
            <View testID="checkout-return-pending">
              <Text variant="headlineSmall" style={styles.title}>
                {t('checkoutReturn.pendingTitle')}
              </Text>
              <Text variant="bodyMedium" style={styles.description}>
                {t('checkoutReturn.pendingDescription')}
              </Text>
              <Button mode="contained" onPress={goToDashboard} style={styles.button}>
                {t('checkoutReturn.returnToDashboard')}
              </Button>
            </View>
          ) : null}

          {phase === 'canceled' ? (
            <View testID="checkout-return-canceled">
              <Text variant="headlineSmall" style={styles.title}>
                {t('checkoutReturn.canceledTitle')}
              </Text>
              <Text variant="bodyMedium" style={styles.description}>
                {t('checkoutReturn.canceledDescription')}
              </Text>
              <Button mode="contained" onPress={goToSelfPay} style={styles.button}>
                {t('checkoutReturn.retry')}
              </Button>
            </View>
          ) : null}

          {phase === 'error' ? (
            <View testID="checkout-return-error">
              <Text variant="headlineSmall" style={[styles.title, { color: theme.colors.error }]}>
                {t('checkoutReturn.errorTitle')}
              </Text>
              <Text variant="bodyMedium" style={styles.description}>
                {t('checkoutReturn.errorDescription')}
              </Text>
              <Button mode="contained" onPress={goToDashboard} style={styles.button}>
                {t('checkoutReturn.returnToDashboard')}
              </Button>
            </View>
          ) : null}
        </Surface>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  center: { flex: 1, justifyContent: 'center', padding: 16 },
  content: { borderRadius: 8, padding: 20 },
  title: { fontWeight: '600', marginBottom: 8, textAlign: 'center' },
  description: { marginBottom: 14, opacity: 0.8, textAlign: 'center' },
  button: { marginTop: 6 },
});
